import {
  Children, cloneElement, isValidElement, ReactNode, ReactElement, SyntheticEvent, ChangeEvent,
} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';

type BatchRow = Record<string, string>;

interface BatchListError {
  rowIndex: number;
  [field: string]: unknown;
}

interface BatchListChange {
  name?: string | undefined;
  value: BatchRow[];
}

interface BatchListProps {
  children: ReactNode;
  items: BatchRow[];
  name?: string | undefined;
  onChange: (event: SyntheticEvent, change: BatchListChange) => void;
  sizes: number[];
  errors?: BatchListError[] | undefined;
  errorMessages?: BatchListError[] | undefined;
  controlVisibility?: string[] | undefined;
}

const getChildName = (child: ReactNode): string => (
  (child as ReactElement<{ name: string }>).props.name
);

export default function BatchList(props: BatchListProps) {
  const {
    children,
    items,
    name,
    onChange,
    sizes,
    errors,
    errorMessages,
    controlVisibility,
  } = props;

  if (Children.toArray(children).some((c) => !isValidElement(c))) {
    throw new Error('items of BatchList should be valid elements');
  }

  let itemKeys = Children.toArray(children).map((child) => getChildName(child));
  if (controlVisibility) {
    itemKeys = itemKeys.filter((k) => controlVisibility.includes(k));
  }

  const renderContainer = (index: number, rowChildren: ReactNode) => (
    <Grid key={`batch_list_row_${index}`} container spacing={2}>
      {rowChildren}
    </Grid>
  );

  const renderItems = (row: BatchRow, rowChildren: ReactNode, index: number) => {
    const isAdd = (index === items.length - 1);
    const isDelete = (index !== items.length - 1);
    const error: Record<string, unknown> = (errors && errors.find((err) => err.rowIndex === index))
      || {};
    const errorMessage: Record<string, unknown> = (errorMessages
      && errorMessages.find((err) => err.rowIndex === index))
      || {};
    const hasError = !!(Object.keys(error).length);

    const elements = Children.toArray(rowChildren).map((child, idx) => {
      const childName = getChildName(child);
      return (
        <Grid key={idx} size={sizes[idx]}>
          {
            cloneElement(child as ReactElement<Record<string, unknown>>, {
              value: row[childName],
              error: error[childName],
              helperText: errorMessage[childName],
              onChange: (event: ChangeEvent<HTMLInputElement>) => {
                row[childName] = event.target.value;
                onChange(event, {
                  name,
                  value: items,
                });
              },
            })
          }
        </Grid>
      );
    });

    elements.push(
      <Grid key="control" size={2}>
        <Button
          data-testid="add"
          variant="outlined"
          disabled={hasError}
          onClick={(event) => {
            if (isAdd) {
              if (hasError) {
                return;
              }

              if (itemKeys.some((k) => row[k]!.toString().trim() === '')) {
                return;
              }

              items.push(itemKeys.reduce<BatchRow>((acc, key) => {
                acc[key] = '';
                return acc;
              }, {}));

              onChange(event, {
                name,
                value: items,
              });
            } else if (isDelete) {
              items.splice(index, 1);
              onChange(event, {
                name,
                value: items,
              });
            }
          }}
        >
          {isAdd && 'Add'}
          {isDelete && 'Delete'}
        </Button>
      </Grid>,
    );

    return elements;
  };

  return (
    <Box
      data-testid={name}
      sx={{ mb: 1 }}
    >
      {
        items.map((row, index) => {
          return renderContainer(
            index,
            renderItems(row, children, index),
          );
        })
      }
    </Box>
  );
}
